import React, { useState, useEffect, useRef } from "react";
import styles from "../../../assets/styles/AuthComponents.module.scss";
import { Link, useNavigate } from "react-router-dom";
import Input from "../../../components/common/input/Input";
import Textarea from "../../../components/common/textarea/Textarea";
import Select from "../../../components/common/select/Select";
import { RegisterPayload } from "../../../api/types/auth.types";
import AuthHeader from "../../../components/common/header/auth-header/AuthHeader";
import AuthService from "../../../api/services/auth.service";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import { useAuth } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { setCookie } from "../../../utils/cookies";
import MapLocationPicker from "../../../components/common/map-location-picker/MapLocationPicker";
import LocationService from "../../../api/services/locations.service";

const Register = () => {
  const { t, i18n } = useTranslation("translation");
  const { setEmail } = useAuth();
  const [currentStage, setCurrentStage] = useState(1);
  const [formValues, setFormValues] = useState<Partial<RegisterPayload>>({});
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const [errors, setErrors] = useState<
    Partial<Record<keyof RegisterPayload, string>>
  >({});
  const [loading, setLoading] = useState(false);
  const [passwordInfo, setPasswordInfo] = useState({
    hasMinLen: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSymbol: false,
  });

  // Countries and cities
  const [countries, setCountries] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedCity, setSelectedCity] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Track if user has attempted to submit stage 3
  const stage3SubmittedRef = useRef(false);

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await LocationService.countries(1, 100);
        if (response.data?.data) {
          setCountries(
            response.data.data.map((c: any) => ({ id: c.id, name: c.name })),
          );
        }
      } catch (error) {
        console.error("Error loading countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };
    loadCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    if (selectedCountry) {
      setLoadingCities(true);
      setCities([]);
      setSelectedCity(null);
      LocationService.cities(selectedCountry.id, 1, 100)
        .then((response) => {
          if (response.data?.data) {
            setCities(
              response.data.data.map((c: any) => ({ id: c.id, name: c.name })),
            );
          }
        })
        .catch((error) => {
          console.error("Error loading cities:", error);
        })
        .finally(() => {
          setLoadingCities(false);
        });
    } else {
      setCities([]);
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  // Clear errors when stage changes
  useEffect(() => {
    if (currentStage === 3) {
      // Reset the submission flag when entering stage 3
      stage3SubmittedRef.current = false;
      // Clear all stage 3 errors when entering stage 3
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.email;
        delete newErrors.contact_phone;
        delete newErrors.password;
        delete newErrors.confirm_password;
        return newErrors;
      });
    } else if (currentStage === 2) {
      // Clear all stage 2 errors when entering stage 2
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.country_id;
        delete newErrors.city_id;
        delete newErrors.business_address;
        delete newErrors.latitude;
        return newErrors;
      });
    }
  }, [currentStage]);

  const handleChange = (
    field: keyof RegisterPayload,
    value: string | number | null,
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const validateStage1 = () => {
    let newErrors: Partial<Record<keyof RegisterPayload, string>> = {};
    if (!formValues?.name?.trim()) {
      newErrors.name = t("required.name") || "";
    }
    if (!formValues?.description?.trim()) {
      newErrors.description = t("required.description") || "";
    }
    if (!formValues?.preferred_language?.trim()) {
      newErrors.preferred_language = t("required.preferred_language") || "";
    }
    // Only update stage 1 errors, preserve other errors
    setErrors((prev) => {
      const updated = { ...prev };
      // Clear stage 1 errors
      delete updated.name;
      delete updated.description;
      delete updated.preferred_language;
      // Add new stage 1 errors
      return { ...updated, ...newErrors };
    });
    return Object.keys(newErrors).length === 0;
  };

  const validateStage2 = () => {
    let newErrors: Partial<Record<keyof RegisterPayload, string>> = {};
    if (!selectedCountry) {
      newErrors.country_id = t("required.country") || "Country is required";
    }
    if (!selectedCity) {
      newErrors.city_id = t("required.city") || "City is required";
    }
    if (!formValues?.business_address?.trim()) {
      newErrors.business_address = t("required.business_address") || "";
    }
    if (!formValues.latitude || !formValues.longitude) {
      newErrors.latitude =
        t("required.location") || "Please select a location on the map";
    }
    // Only update stage 2 errors, preserve other errors
    setErrors((prev) => {
      const updated = { ...prev };
      // Clear stage 2 errors
      delete updated.country_id;
      delete updated.city_id;
      delete updated.business_address;
      delete updated.latitude;
      // Add new stage 2 errors
      return { ...updated, ...newErrors };
    });
    return Object.keys(newErrors).length === 0;
  };

  const validateStage3 = () => {
    stage3SubmittedRef.current = true;
    let newErrors: Partial<Record<keyof RegisterPayload, string>> = {};
    if (!formValues?.email?.trim()) {
      newErrors.email = t("required.email") || "";
    }
    if (!formValues?.contact_phone?.trim()) {
      newErrors.contact_phone = t("required.phone") || "";
    }
    if (!formValues.confirm_password) {
      newErrors.confirm_password = t("required.confrim_password") || "";
    }
    if (formValues.confirm_password !== formValues.password) {
      newErrors.confirm_password = t("required.password_match") || "";
    }
    if (!formValues.password) {
      newErrors.password = t("required.password") || "";
    }
    setErrors((prev) => {
      // Only update stage 3 errors, preserve other errors
      const updated = { ...prev };
      delete updated.email;
      delete updated.contact_phone;
      delete updated.password;
      delete updated.confirm_password;
      return { ...updated, ...newErrors };
    });
    return Object.keys(newErrors).length === 0;
  };

  function validatePassword(password: string) {
    const rules = {
      hasMinLen: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSymbol: /[!@#$%^&*(),.?":{}|<>_\-/\\;'[\]+=`~]/.test(password),
    };
    let error: string | null = null;
    if (!rules.hasMinLen) error = t("password.errorMinLen");
    else if (!rules.hasUpper) error = t("password.errorUpper");
    else if (!rules.hasLower) error = t("password.errorLower");
    else if (!rules.hasNumber) error = t("password.errorNumber");
    else if (!rules.hasSymbol) error = t("password.errorSymbol");
    return { rules, error };
  }

  const handleNext = () => {
    if (currentStage === 1) {
      if (validateStage1()) {
        setCurrentStage(2);
      }
    } else if (currentStage === 2) {
      if (validateStage2()) {
        setCurrentStage(3);
      }
    }
  };

  const handleBack = () => {
    if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStage !== 3) {
      handleNext();
      return;
    }

    if (!validateStage3()) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", formValues.email || "");
      formData.append("password", formValues.password || "");
      formData.append("business_address", formValues.business_address || "");
      formData.append("confirm_password", formValues.confirm_password || "");
      formData.append("contact_phone", formValues.contact_phone || "");
      formData.append("description", formValues.description || "");
      formData.append("name", formValues.name || "");
      formData.append(
        "preferred_language",
        formValues.preferred_language || "",
      );

      if (formValues.city_id) {
        formData.append("city_id", formValues.city_id.toString());
      }
      if (formValues.country_id) {
        formData.append("country_id", formValues.country_id.toString());
      }
      if (formValues.latitude) {
        formData.append("latitude", formValues.latitude.toString());
      }
      if (formValues.longitude) {
        formData.append("longitude", formValues.longitude.toString());
      }

      const response = await AuthService.register(formData);
      setEmail(formValues.email || "");
      // With OTP confirmation disabled server-side the store comes back
      // already active, so there is no code to ask for.
      if (response.data?.otp_required === false) {
        navigate("/auth/login");
      } else {
        navigate("/auth/otp");
      }
      setCookie("lux_store_lang", formValues.preferred_language, 365);
      await i18n.changeLanguage(formValues.preferred_language);
    } catch (error: any) {
      setLoading(false);
      const message = getErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  // Map API errors to translated messages
  const getErrorMessage = (error: any): string => {
    const statusCode = error?.response?.status;
    const apiMessage =
      error?.response?.data?.message || error?.response?.data?.error || "";

    // Map status codes and messages to translation keys
    if (statusCode === 404 || statusCode === 400) {
      const lowerMessage = apiMessage.toLowerCase();
      if (
        lowerMessage.includes("email already exists") ||
        lowerMessage.includes("email already exist")
      ) {
        return t("errors.email_already_exists") || "Email already exists";
      }
      if (
        lowerMessage.includes("phone already exists") ||
        lowerMessage.includes("phone already exist")
      ) {
        return (
          t("errors.phone_already_exists") || "Phone number already exists"
        );
      }
      if (lowerMessage.includes("invalid email")) {
        return t("invalid_email_format") || "Invalid email format";
      }
    }

    if (statusCode === 401 || statusCode === 403) {
      return t("invalid_credentials") || "Invalid credentials";
    }

    if (statusCode === 429) {
      return (
        t("error.too_many_requests") ||
        "Too many requests. Please try again later."
      );
    }

    // Default to generic error message
    return (
      t("error_message") || "An unexpected error occurred. Please try again."
    );
  };

  const handleLocationChange = (lat: number, lng: number, address: string) => {
    handleChange("latitude", lat);
    handleChange("longitude", lng);
    handleChange("business_address", address);
  };

  const handleCountryChange = (
    country: { id: string; name: string } | null,
  ) => {
    if (country) {
      const countryObj = { id: parseInt(country.id), name: country.name };
      setSelectedCountry(countryObj);
      handleChange("country_id", countryObj.id);
      handleChange("city_id", null);
    } else {
      setSelectedCountry(null);
      handleChange("country_id", null);
      handleChange("city_id", null);
    }
  };

  const handleCityChange = (city: { id: string; name: string } | null) => {
    if (city) {
      const cityObj = { id: parseInt(city.id), name: city.name };
      setSelectedCity(cityObj);
      handleChange("city_id", cityObj.id);
    } else {
      setSelectedCity(null);
      handleChange("city_id", null);
    }
  };

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("login.sign_up")} />
      <form
        className={`${styles.form} ${styles.register_form}`}
        onSubmit={handleSubmit}
      >
        <AuthHeader title={t("login.sign_up")} />
        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

        {/* Stage Indicator */}
        <div
          className={`${styles.stage_indicator} ${currentStage === 2 ? styles.stage_2_indicator : ""}`}
        >
          <div
            className={`${styles.stage} ${currentStage >= 1 ? styles.active : ""}`}
          >
            <div className={styles.stage_number}>1</div>
            <div className={styles.stage_label}>
              {t("register.stage1") || "Store Info"}
            </div>
          </div>
          <div className={styles.stage_line}></div>
          <div
            className={`${styles.stage} ${currentStage >= 2 ? styles.active : ""}`}
          >
            <div className={styles.stage_number}>2</div>
            <div className={styles.stage_label}>
              {t("register.stage2") || "Address"}
            </div>
          </div>
          <div className={styles.stage_line}></div>
          <div
            className={`${styles.stage} ${currentStage >= 3 ? styles.active : ""}`}
          >
            <div className={styles.stage_number}>3</div>
            <div className={styles.stage_label}>
              {t("register.stage3") || "Account"}
            </div>
          </div>
        </div>

        <div
          className={`${styles.form_content} ${currentStage === 2 ? styles.stage_2_content : ""}`}
        >
          {/* Stage 1: Store Info */}
          {currentStage === 1 && (
            <>
              <div className={styles.stage_info}>
                <p>
                  {t("register.stage1_description") ||
                    "Enter your store information. This will be displayed to customers."}
                </p>
              </div>
              <Input
                label={t("labels.store_name") || ""}
                name="name"
                placeholder={t("placeholders.store_name") || ""}
                type="text"
                id="name"
                value={formValues.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={styles.input}
                error={errors.name}
              />
              <Textarea
                label={t("labels.description") || ""}
                name="description"
                placeholder={t("placeholders.description") || ""}
                id="description"
                value={formValues.description}
                onChange={(e) => handleChange("description", e.target.value)}
                className={styles.input}
                error={errors.description}
              />
              <Select
                value={formValues.preferred_language || ""}
                options={[
                  { id: "az", name: t("languages.az") },
                  { id: "en", name: t("languages.en") },
                  { id: "de", name: t("languages.de") },
                  { id: "es", name: t("languages.es") },
                ]}
                label={t("labels.preferred_language") || ""}
                onChange={(value) => {
                  if (value) {
                    handleChange("preferred_language", value.id);
                  }
                }}
                className={styles.input}
                error={errors.preferred_language}
              />
            </>
          )}

          {/* Stage 2: Address */}
          {currentStage === 2 && (
            <>
              <div className={styles.country_city_row}>
                <Select
                  value={selectedCountry ? selectedCountry.id.toString() : ""}
                  options={countries.map((c) => ({
                    id: c.id.toString(),
                    name: c.name,
                  }))}
                  label={t("labels.country") || "Country"}
                  onChange={handleCountryChange}
                  className={styles.input}
                  error={errors.country_id}
                  loading={loadingCountries}
                />
                <Select
                  value={selectedCity ? selectedCity.id.toString() : ""}
                  options={cities.map((c) => ({
                    id: c.id.toString(),
                    name: c.name,
                  }))}
                  label={t("labels.city") || "City"}
                  onChange={handleCityChange}
                  className={styles.input}
                  error={errors.city_id}
                  disabled={!selectedCountry}
                  loading={loadingCities}
                />
              </div>
              <div className={styles.map_section}>
                <MapLocationPicker
                  latitude={formValues.latitude || null}
                  longitude={formValues.longitude || null}
                  address={formValues.business_address || undefined}
                  cityName={selectedCity?.name || null}
                  countryName={selectedCountry?.name || null}
                  onLocationChange={handleLocationChange}
                  disabled={!selectedCity || !selectedCountry}
                />
                {errors.latitude && (
                  <p className={styles.error}>{errors.latitude}</p>
                )}
              </div>
            </>
          )}

          {/* Stage 3: Account */}
          {currentStage === 3 && (
            <>
              <div className={styles.stage_info}>
                <p>
                  {t("register.stage3_description") ||
                    "Create your account credentials."}
                </p>
              </div>
              <Input
                label={t("labels.email") || ""}
                name="email"
                placeholder={t("placeholders.email") || ""}
                type="email"
                id="email"
                value={formValues.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className={styles.input}
                error={stage3SubmittedRef.current ? errors.email : undefined}
              />
              <Input
                label={t("labels.phone") || ""}
                name="contact_phone"
                placeholder={t("placeholders.phone") || ""}
                type="phone"
                id="contact_phone"
                value={formValues.contact_phone}
                onChange={(e) => handleChange("contact_phone", e.target.value)}
                className={styles.input}
                error={
                  stage3SubmittedRef.current ? errors.contact_phone : undefined
                }
                phone
              />
              <Input
                label={t("labels.password") || ""}
                name="password"
                placeholder="••••••••••"
                type="password"
                id="password"
                value={formValues.password}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange("password", value);
                  const validation = validatePassword(value);
                  setPasswordInfo(validation.rules);
                  // Only show password validation error if user has typed something
                  if (value && validation.error) {
                    setErrors((prev) => ({
                      ...prev,
                      password: validation.error || "",
                    }));
                  } else {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.password;
                      return newErrors;
                    });
                  }
                  // Re-validate confirm password if it has a value
                  if (formValues.confirm_password) {
                    if (formValues.confirm_password !== value) {
                      setErrors((prev) => ({
                        ...prev,
                        confirm_password:
                          t("required.password_match") ||
                          "Passwords do not match",
                      }));
                    } else {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.confirm_password;
                        return newErrors;
                      });
                    }
                  }
                }}
                className={styles.input}
                error={stage3SubmittedRef.current ? errors.password : undefined}
              />
              <Input
                label={t("labels.confrim_password") || ""}
                type="password"
                id="confirm_password"
                placeholder="••••••••••"
                value={formValues.confirm_password}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange("confirm_password", value);
                  // Check password match in real-time (always show this error if passwords don't match)
                  if (
                    value &&
                    formValues.password &&
                    value !== formValues.password
                  ) {
                    setErrors((prev) => ({
                      ...prev,
                      confirm_password:
                        t("required.password_match") ||
                        "Passwords do not match",
                    }));
                  } else if (
                    value &&
                    formValues.password &&
                    value === formValues.password
                  ) {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.confirm_password;
                      return newErrors;
                    });
                  }
                }}
                className={styles.input}
                error={
                  stage3SubmittedRef.current
                    ? errors.confirm_password
                    : undefined
                }
              />
              <div className={styles.passwordInfo}>
                <p
                  className={
                    passwordInfo.hasMinLen ? styles.valid : styles.invalid
                  }
                >
                  • {t("password.minLen")}
                </p>
                <p
                  className={
                    passwordInfo.hasUpper ? styles.valid : styles.invalid
                  }
                >
                  • {t("password.upper")}
                </p>
                <p
                  className={
                    passwordInfo.hasLower ? styles.valid : styles.invalid
                  }
                >
                  • {t("password.lower")}
                </p>
                <p
                  className={
                    passwordInfo.hasNumber ? styles.valid : styles.invalid
                  }
                >
                  • {t("password.number")}
                </p>
                <p
                  className={
                    passwordInfo.hasSymbol ? styles.valid : styles.invalid
                  }
                >
                  • {t("password.symbol")}
                </p>
              </div>
            </>
          )}
        </div>

        <div
          className={`${styles.form_actions} ${currentStage === 2 ? styles.stage_2_actions : ""}`}
        >
          {currentStage > 1 && (
            <button
              type="button"
              className={`${styles.button} ${styles.button_secondary}`}
              onClick={handleBack}
              disabled={loading}
            >
              {t("buttons.back") || "Back"}
            </button>
          )}
          <button
            type={currentStage === 3 ? "submit" : "button"}
            className={styles.button}
            onClick={(e) => {
              if (currentStage < 3) {
                e.preventDefault();
                handleNext();
              }
            }}
            disabled={loading}
          >
            {loading
              ? t("buttons.loading")
              : currentStage === 3
                ? t("buttons.register")
                : t("buttons.next") || "Next"}
          </button>
        </div>

        <div className={styles.account}>
          {t("login.have_account")}
          <Link to={"/auth/login"} className={styles.link}>
            {t("buttons.login")}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default Register;
